import { Link } from 'react-router-dom'
import type { FacilityProfileCardModel } from '@buxus/shared'
import { formatPhoneDisplay, phoneTelHref } from '@buxus/shared'
import type { ScreenInteractionContext } from './platform/analytics/interactionAnalytics'
import { trackLinkPressed, trackPhonePressed } from './platform/analytics/interactionAnalytics'
import { pathForMenuStackPrefix } from './navigation/appPaths'

type FacilityProfileCardProps = {
  model: FacilityProfileCardModel
  operatorId: string
  activeAssetId: string
  urlSlugForOperator: (operatorId: string) => string
  onMapJump?: () => void
  analytics?: ScreenInteractionContext
}

function PhoneLine({
  label,
  value,
  analytics,
}: {
  label: string
  value: string
  analytics?: ScreenInteractionContext
}) {
  const display = formatPhoneDisplay(value)
  const href = phoneTelHref(value)
  return (
    <div className="facility-profile-card-row">
      <span className="facility-profile-card-label">{label}</span>
      {href ? (
        <a
          className="buxus-theme-link"
          href={href}
          onClick={analytics ? () => trackPhonePressed({ ...analytics, phone: value, contact: label }) : undefined}
        >
          {display}
        </a>
      ) : (
        <span className="facility-profile-card-value">{display}</span>
      )}
    </div>
  )
}

export function FacilityProfileCard({
  model,
  operatorId,
  activeAssetId,
  urlSlugForOperator,
  onMapJump,
  analytics,
}: FacilityProfileCardProps) {
  const overviewTo = pathForMenuStackPrefix(
    operatorId,
    ['screen.assets', 'screen.assetOverview'],
    activeAssetId,
    urlSlugForOperator,
  )

  return (
    <section className="facility-profile-card" aria-label="Facility overview">
      <h3 className="facility-profile-card-title">{model.assetName}</h3>
      <div className="facility-profile-card-body">
        {model.addressLines.length > 0 ? (
          <div className="facility-profile-card-address">
            {model.addressLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        ) : null}
        {model.latLongLabel ? (
          <div className="facility-profile-card-row">
            <span className="facility-profile-card-label">Lat / Long:</span>
            {model.canJumpToMap ? (
              <Link
                className="buxus-theme-link key-location-coords"
                to={overviewTo}
                onClick={() => {
                  if (analytics && model.latLongLabel) {
                    trackLinkPressed({
                      ...analytics,
                      linkTarget: overviewTo,
                      title: model.latLongLabel,
                      navigationReason: 'facility_coordinates',
                    })
                  }
                  onMapJump?.()
                }}
              >
                {model.latLongLabel}
              </Link>
            ) : (
              <span className="facility-profile-card-value">{model.latLongLabel}</span>
            )}
          </div>
        ) : null}
        {model.countiesLabel ? (
          <div className="facility-profile-card-row facility-profile-card-row-multiline">
            <span className="facility-profile-card-label">Counties:</span>
            <span className="facility-profile-card-value facility-profile-card-multiline">
              {model.countiesLabel.split('\n').map((line) => (
                <span key={line} className="facility-profile-card-multiline-line">
                  {line}
                </span>
              ))}
            </span>
          </div>
        ) : null}
        {model.emergencyPhone ? (
          <PhoneLine label="24-Hour Emergency Contact:" value={model.emergencyPhone} analytics={analytics} />
        ) : null}
        {model.phoneNumber1 ? (
          <PhoneLine label="Phone Number 1:" value={model.phoneNumber1} analytics={analytics} />
        ) : null}
        {model.phoneNumber2 ? (
          <PhoneLine label="Phone Number 2:" value={model.phoneNumber2} analytics={analytics} />
        ) : null}
        {model.facilityDescription ? (
          <div className="facility-profile-card-row facility-profile-card-row-multiline">
            <span className="facility-profile-card-label">Facility Description:</span>
            <span className="facility-profile-card-value facility-profile-card-multiline">
              {model.facilityDescription}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  )
}
